import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  completePasswordReset,
  validatePasswordReset,
} from "../../api/clientPortalPublic";
import { formatApiError } from "../../utils/formatApiError";

export default function PortalResetPassword() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();
  const salonIdParam =
    (searchParams.get("salonId") || searchParams.get("salon_id") || "").trim();
  const salonId = useMemo(() => Number(salonIdParam || 0), [salonIdParam]);

  const [loading, setLoading] = useState(true);
  const [validInfo, setValidInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token || !salonId) {
        setLoading(false);
        setValidInfo(null);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await validatePasswordReset(token, salonId);
        if (!active) return;
        if (data?.valid) {
          setValidInfo(data);
        } else {
          setValidInfo(null);
        }
      } catch (err) {
        if (!active) return;
        setValidInfo(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [salonId, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await completePasswordReset({
        salon_id: salonId,
        token,
        password,
      });
      setSuccess(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Password reset error:", err);
      }
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const showInvalid = !loading && !validInfo;

  return (
    <div className="px-4 py-6">
      <div className="mx-auto w-full max-w-[1400px] text-base md:text-lg">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Client Portal
            </h1>
            <p className="text-sm md:text-base text-rose-100">Reset Password</p>
          </div>
          <div className="px-6 py-8">
            <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-6">
              {loading && (
                <div className="text-sm text-slate-600">Loading...</div>
              )}

              {showInvalid && (
                <div className="text-center">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                    This reset link is invalid or expired.
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    Please request a new password reset link.
                  </p>
                </div>
              )}

              {!loading && validInfo && success && (
                <div className="text-center">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                    Password updated
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    You can sign in with your new password.
                  </p>
                  <div className="mt-4 flex justify-center">
                    <Link
                      to={`/portal/salon/${salonId}/login`}
                      className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800"
                    >
                      Back to login
                    </Link>
                  </div>
                </div>
              )}

              {!loading && validInfo && !success && (
                <div>
                  <div className="space-y-2">
                    <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                      Create a new password
                    </h2>
                    <p className="text-sm md:text-base text-slate-600">
                      Choose a new password for your account.
                    </p>
                    {validInfo?.name && (
                      <p className="text-xs text-slate-500">
                        Account: {validInfo.name}
                      </p>
                    )}
                    {validInfo?.email_masked && (
                      <p className="text-xs text-slate-500">
                        Email on file: {validInfo.email_masked}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-600">
                        New password
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 px-4"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-600">
                        Confirm password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 px-4"
                        required
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="submit"
                        disabled={saving}
                        className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {saving ? "Saving..." : "Update password"}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
